/**
 * SFrame230 — Execute personal property; source sframe-160, executes sframe-230.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SettlementStepPage } from '../../settlement';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import { extractPersonalPropertyFrom160, type StreamJUnifiedStatus } from './streamJSources';
import { RowDocumentPanel } from './streamJRowDocs';

const step = SETTLEMENT_PHASE_CONFIGS.distribute.steps.find((s) => s.id === 'dist-3')!;
const STAT: StreamJUnifiedStatus[] = ['not_started', 'in_progress', 'completed', 'blocked'];
const LAB: Record<StreamJUnifiedStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};
type Tab = 'all' | StreamJUnifiedStatus;
type Exec = { status: StreamJUnifiedStatus; notes: string; blockerReason: string };

function parse230(raw: unknown): Record<string, Exec> {
  const out: Record<string, Exec> = {};
  if (!raw || typeof raw !== 'object') return out;
  const ex = (raw as { executions?: unknown }).executions;
  if (!ex || typeof ex !== 'object') return out;
  for (const [id, row] of Object.entries(ex)) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const st = r.status as StreamJUnifiedStatus;
    const status =
      STAT.indexOf(st as StreamJUnifiedStatus) >= 0 ? st : ('not_started' as StreamJUnifiedStatus);
    out[id] = {
      status,
      notes: String(r.notes ?? ''),
      blockerReason: String(r.blockerReason ?? ''),
    };
  }
  return out;
}

export default function SFrame230() {
  const { estateId: param } = useParams<{ estateId: string }>();
  const estateId = param ?? null;
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId, { readOnly: isArchived });

  const [src, setSrc] = useState<ReturnType<typeof extractPersonalPropertyFrom160>>([]);
  const [exec, setExec] = useState<Record<string, Exec>>({});
  const [tab, setTab] = useState<Tab>('all');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    if (!estateId) {
      setSrc([]);
      setExec({});
      setReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const [inv, st] = await Promise.all([
        loadFrameData('sframe-160'),
        loadFrameData('sframe-230'),
      ]);
      if (cancelled) return;
      const rows = extractPersonalPropertyFrom160((inv?.data as Record<string, unknown>) ?? null);
      setSrc(rows);
      setExec(parse230(st?.data));
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  const gx = useCallback(
    (id: string): Exec => exec[id] ?? { status: 'not_started', notes: '', blockerReason: '' },
    [exec]
  );

  const setRow = useCallback(
    (id: string, p: Partial<Exec>) => {
      if (isArchived) return;
      setExec((prev) => {
        const cur = prev[id] ?? { status: 'not_started' as StreamJUnifiedStatus, notes: '', blockerReason: '' };
        return { ...prev, [id]: { ...cur, ...p } };
      });
    },
    [isArchived]
  );

  useEffect(() => {
    if (!estateId || isArchived || !ready) return;
    const t = window.setTimeout(() => void saveFrameData('sframe-230', { version: 1, executions: exec }), 520);
    return () => window.clearTimeout(t);
  }, [exec, estateId, isArchived, ready, saveFrameData]);

  const filtered = useMemo(() => {
    if (tab === 'all') return src;
    return src.filter((r) => gx(r.id).status === tab);
  }, [src, tab, gx]);

  const prog = useMemo(() => {
    const n = src.length;
    if (!n) return { ok: 0, n };
    const ok = src.filter((r) => gx(r.id).status === 'completed').length;
    return { ok, n };
  }, [src, gx]);

  const empty = ready && src.length === 0;
  const ei = estateId ?? '';

  return (
    <SettlementStepPage
      step={step}
      phase="distribute"
      overviewRoute="/sframe-220"
      plannerFrameIds={['frame-246']}
      settlementDocumentContext={{ phase: 'distribute', stepId: 'dist-3' }}
    >
      <div className="space-y-4 mb-12">
        <p className="text-sm text-stone-700 max-w-3xl">
          Read-only columns sync from settlement frame <code className="text-xs">sframe-160</code>. Executor execution fields persist to{' '}
          <code className="text-xs">sframe-230</code>.
        </p>
        {!empty && (
          <div className="max-w-xl space-y-1">
            <p className="text-xs uppercase text-stone-600">Personal property rollout</p>
            <div className="h-2 rounded-full bg-stone-200 overflow-hidden">
              <div className="h-full bg-blue-900" style={{ width: `${prog.n ? Math.round((prog.ok / prog.n) * 100) : 0}%` }} />
            </div>
            <p className="text-sm text-stone-800">
              {prog.ok} of {prog.n} rows completed
            </p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {(['all'] as Tab[]).concat(STAT).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`px-4 py-1.5 rounded-full text-sm border font-semibold ${tab === k ? 'bg-blue-900 text-white border-blue-950' : 'border-stone-300 text-stone-700 bg-white'}`}
            >
              {k === 'all' ? 'All' : LAB[k]}
            </button>
          ))}
        </div>
        {empty ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm">
            <p className="font-semibold mb-2">Personal property inventory unavailable.</p>
            {ei ? (
              <Link className="text-blue-900 font-semibold underline" to={`/sframe-160/${ei}`}>Complete Inventory Step 160 →</Link>
            ) : (
              <span>Open from an estate.</span>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-stone-200">
            <table className="min-w-full text-sm">
              <thead className="bg-stone-100 text-[11px] uppercase text-stone-600">
                <tr>
                  <th className="py-2 px-3 text-left">Item</th>
                  <th className="py-2 px-3 text-left hidden sm:table-cell">Category</th>
                  <th className="py-2 px-3 text-left hidden md:table-cell">Beneficiary</th>
                  <th className="py-2 px-3 text-left hidden lg:table-cell">Method</th>
                  <th className="py-2 px-3 text-left hidden lg:table-cell">Est. value</th>
                  <th className="py-2 px-3 text-left">Status</th>
                  <th className="py-2 px-3 text-left">Notes</th>
                  <th className="py-2 px-3 text-left">Upload</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const e = gx(r.id);
                  return (
                    <Fragment key={r.id}>
                      <tr className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-3 px-3">{r.item}</td>
                        <td className="py-3 px-3 hidden sm:table-cell">{r.category}</td>
                        <td className="py-3 px-3 hidden md:table-cell">{r.beneficiaryDisplay}</td>
                        <td className="py-3 px-3 hidden lg:table-cell">{r.distributionMethod}</td>
                        <td className="py-3 px-3 hidden lg:table-cell">{r.estimatedValue || '—'}</td>
                        <td className="py-3 px-3">
                          <select
                            className="text-sm border border-stone-200 rounded px-2 py-1 bg-white"
                            disabled={!estateId || isArchived}
                            value={e.status}
                            onChange={(ev) => setRow(r.id, { status: ev.target.value as StreamJUnifiedStatus })}
                          >
                            {STAT.map((s) => (
                              <option key={s} value={s}>{LAB[s]}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-3 px-3 align-top max-w-[200px]">
                          <textarea
                            className="w-full rounded border border-stone-200 px-2 py-1 text-xs min-h-[48px]"
                            disabled={isArchived || !estateId}
                            value={e.notes}
                            onChange={(ev) => setRow(r.id, { notes: ev.target.value })}
                          />
                          {e.status === 'blocked' && (
                            <textarea
                              className="mt-1 w-full rounded border border-red-100 px-2 py-1 text-xs min-h-[36px]"
                              disabled={isArchived || !estateId}
                              placeholder="Blocker detail"
                              value={e.blockerReason}
                              onChange={(ev) => setRow(r.id, { blockerReason: ev.target.value })}
                            />
                          )}
                        </td>
                        <td className="py-3 px-3 align-top">
                          <RowDocumentPanel estateId={estateId} phase="distribute" stepId={`exec-230:${r.id}`} readOnly={isArchived} />
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SettlementStepPage>
  );
}
